import { ChangeDetectorRef, Component, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { FlujoCreditoLightService } from 'src/app/shared/services/flujo-credito-light.service';
import { MatDialog } from '@angular/material/dialog';
import { PlantillaSalfaFcLightComponent } from '../fichas-clientes/plantilla-salfa-fc-light/plantilla-salfa-fc-light.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { IApprovalFlowItem, IApprovalFlowResponse, IApprovalFlowState, IFlujoLightTXPermissions, IGeneralReport, IMonto, IResultado, IRuleEngineField, IRuleEngineFieldWithValue } from 'src/app/interface/IFlujoLightTx';
import { FlujoCreditoLightStages, FlujoCreditoLightStatusTX, STEP_STATUS } from 'src/app/enum/EFlujoCreditoLight';
import { MatStepper } from '@angular/material/stepper';
import { IFile } from 'src/app/interface/IFilesManager';
import { PdfGeneratorService } from 'src/app/shared/services/pdf-generator.service';
import { Subscription } from 'rxjs';
import { AlertService } from 'src/app/components/_alert/alert.service';
import { goToTop } from '../utils/utils';
import { SessionService } from 'src/app/shared/services/session.service';
import { ESystemAccess } from 'src/app/enum/EAccess';
import { IUserInfoCognito } from 'src/app/shared/services/cognito.service';

@Component({
    selector: 'app-flujo-credito-light-approval-flow',
    templateUrl: './flujo-credito-light-approval-flow.component.html',
    styleUrls: ['./flujo-credito-light-approval-flow.component.scss']
})
export class FlujoCreditoLightApprovalFlowComponent {

    private canAccessFlujoCreditoLight = false;

    isAdmin = false;
    currentUser = '';
    isApproverUser = false;

    PDF_REPORT_NAME = 'flujoCreditoLight-fichaCliente';
    permissions: IFlujoLightTXPermissions[] = [];
    currentModel = '';
    showDetailsStep2 = false;
    currentIdTransaccion = '';
    attachedFiles: IFile[] = [];

    camposDataSource: MatTableDataSource<any> = new MatTableDataSource<any>;

    camposColumns: string[] = [
        'producto',
        'montoResuelto',
    ];

    montosForm = this.fb.group({});
    otrosForm = this.fb.group({});
    commentsForm = this.fb.group({
        comments: [null, Validators.required],
    });

    otrosData = [];

    flujoCreditoLightStages = FlujoCreditoLightStages;
    currentStage: FlujoCreditoLightStages = FlujoCreditoLightStages.FICHA_CLIENTE;

    isFileUploading = false;
    isPDFLoading = false;

    generalReport!: IGeneralReport;

    approvalFlow?: IApprovalFlowResponse;
    selectedApprovalFlowItem?: IApprovalFlowItem;
    previousApprovalFlowItem?: IApprovalFlowItem;
    currentApprovalFlowItem?: IApprovalFlowItem;
    currentStep?: number;

    stepStatus = STEP_STATUS;

    selectedStep?: number;

    currentVersion = 2;

    isApproveLoading = false;
    isRejectLoading = false;

    currentTotalApprovedAmount = 0;
    selectedTotalApprovedAmount = 0;
    lastTotalApprovedAmount = 0;

    private montosFormSubscription!: Subscription;

    ruleEngineFields: IRuleEngineField[] = [];
    ruleEngineFieldsWithValue: IRuleEngineFieldWithValue[] = [];

    readonly FIELD_TYPES = {
        STRING: 'string',
        NUMBER: 'number',
        BOOLEAN: 'boolean',
    } as const

    @ViewChild('stepper') stepper?: MatStepper;

    constructor(
        private flujoCreditoLightService: FlujoCreditoLightService,
        private fb: FormBuilder,
        private route: ActivatedRoute,
        public dialog: MatDialog,
        private snackBar: MatSnackBar,
        private router: Router,
        private pdfGeneratorService: PdfGeneratorService,
        private readonly changeDetectorRef: ChangeDetectorRef,
        private alertService: AlertService,
        private _sessionService: SessionService
    ) { }

    ngOnInit(): void {
        this.canAccessFlujoCreditoLight = this.setCanAccessFlujoCreditoLight();

        if (!this.canAccessFlujoCreditoLight) {
            this.router.navigate(['/request-access']);
            return;
        }

        this.currentIdTransaccion = this.route.snapshot.paramMap.get('idTransaccion') || '';

        this.isAdmin = this._sessionService.getUserAccess().includes(ESystemAccess.FLUJO_CREDITO_LIGHT_ADMIN);

        const userInfo: IUserInfoCognito = this._sessionService.getUserInfo();
        this.currentUser = userInfo.email;

        this.getPermissions();
        this.getGeneralReport(this.currentIdTransaccion);
        this.getAttachedFiles();

        this.montosFormSubscription = this.montosForm.valueChanges.subscribe(() => {
            this.updateCurrentTotalApprovedAmount();
        });

    }

    setCanAccessFlujoCreditoLight () {
        const userAccess = this._sessionService.getUserAccess();

        const flujoCreditoLightPermission = [
            ESystemAccess.FLUJO_CREDITO_LIGHT,
            ESystemAccess.FLUJO_CREDITO_LIGHT_ADMIN,
        ]

        return flujoCreditoLightPermission.some(permission => userAccess.includes(permission));
    }

    ngAfterViewInit(): void {
        this.getCurrentStage();
    }

    ngAfterViewChecked(): void {
        this.changeDetectorRef.detectChanges();
    }

    ngOnDestroy(): void {
        this.montosFormSubscription?.unsubscribe();
    }

    getPermissions(): void {
        this.flujoCreditoLightService.getPermissions().subscribe(
            permissions => {
                this.permissions = permissions;
            },
            error => {
                this.alertService.error(`Error al obtener permisos: ${error?.error?.message}`);
                goToTop();
            }
        );
    }
    getGeneralReport(idTransaccion: string): void {
        this.flujoCreditoLightService.getGeneralReport(idTransaccion).subscribe(
            report => {
                const transaccionNoCompletada = report.datosBasicosSolicitud.estadoTransaccion !== FlujoCreditoLightStatusTX.COMPLETED;
                const transaccionAutomatica = report.isAutomaticDecision;

                if (transaccionNoCompletada || transaccionAutomatica) {
                    this.router.navigate(['/flujo-credito-light']);
                    this.alertService.error('No es posible cargar el flujo de aprobación.');
                    return;
                }

                this.currentModel = report.datosBasicosSolicitud.modelo;
                this.generalReport = report;

                // Se llaman aquí porque necesitan el currentModel
                this.getRuleEngineFields();
                this.getApprovalFlow();
            },
            error => {
                this.router.navigate(['/flujo-credito-light']);
                this.alertService.error(`Error al obtener reporte general: ${error?.error?.message}`);
                goToTop();
            }
        );
    }

    getAttachedFiles(): void {
        this.flujoCreditoLightService.getAttachedFiles(this.currentIdTransaccion).subscribe(
            response => {
                this.attachedFiles = response.files.map(file => ({
                    id: file.fullFileName,
                    displayName: file.displayName,
                    url: file.url
                }));
            },
            error => {
                this.alertService.error(`Error al obtener archivos adjuntos: ${error?.error?.message}`);
                goToTop();
            }
        );
    }

    validaClave(template: string): boolean {
        if (!this.currentModel) {
            return false;
        }

        if (!this.permissions || this.permissions.length <= 0) {
            return false;
        }

        const permissions = this.permissions.find(permission => permission.template === template);
        if (!permissions || !permissions?.models || permissions.models.length <= 0) {
            return false;
        }

        if (!permissions.models.includes(this.currentModel)) {
            this.flujoCreditoLightService.setModeloNotFound(true);
            this.router.navigate(['/flujo-credito-light']);
            return false;
        }

        return true;
    }

    uploadFile(fileData: { base64Content: string; fileName: string }): void {
        this.isFileUploading = true;
        const payload = {
            idTransaccion: this.currentIdTransaccion,
            base64Content: fileData.base64Content,
            fileName: fileData.fileName
        };

        this.flujoCreditoLightService.uploadFile(payload).subscribe(
            () => {
                this.openSnackBar(`Archivo subido correctamente.`, 'Cerrar');
                this.getAttachedFiles();
                this.isFileUploading = false;
            },
            error => {
                this.alertService.error(`Error al subir archivo: ${error?.error?.message}`);
                goToTop();
            }
        );
    }

    deleteFile(fileData: IFile): void {
        const payload = {
            idTransaccion: this.currentIdTransaccion,
            fullFileName: fileData.id.toString()
        };

        this.flujoCreditoLightService.deleteAttachedFile(payload).subscribe(
            () => {
                this.openSnackBar(`Archivo eliminado correctamente.`, 'Cerrar');
                this.getAttachedFiles();
            },
            error => {
                this.alertService.error(`Error al eliminar archivo: ${error?.error?.message}`);
                goToTop();
            }
        );
    }

    verFichaCliente(): void {
        // Desenfoca el botón activo para evitar problemas de accesibilidad
        const buttonElement = document.activeElement as HTMLElement;
        buttonElement.blur();

        let dialogRef;
        if (this.validaClave('modelo01')) {
            dialogRef = this.dialog.open(PlantillaSalfaFcLightComponent);
        }

        if (!dialogRef) return;

        dialogRef.componentInstance.isModal = true;
        dialogRef.componentInstance.idTransaccion = this.currentIdTransaccion;
        dialogRef.componentInstance.generalReport = this.generalReport;

    }

    openSnackBar(message: string, action: string): void {
        this.snackBar.open(message, action);
    }

    getCurrentStage(): void {
        this.flujoCreditoLightService.getCurrentStage(this.currentIdTransaccion).subscribe(
            stage => {
                if (!stage?.currentStage) {
                    this.createNewStage(FlujoCreditoLightStages.FICHA_CLIENTE);
                } else {
                    this.currentStage = stage.currentStage as FlujoCreditoLightStages;
                    this.setCurrentStage();
                }
            },
            error => {
                this.alertService.error(`Error al obtener etapa actual: ${error?.error?.message}`);
                goToTop();
            }
        );
    }

    createNewStage(stageName: FlujoCreditoLightStages): void {
        const newStage = { idTransaccion: this.currentIdTransaccion, stage: stageName };
        this.flujoCreditoLightService.createStage(newStage).subscribe({
            error: error => {
                this.alertService.error(`Error al crear etapa: ${error?.error?.message}`);
                goToTop();
            }
        });
    }

    setCurrentStage(): void {
        if (!this.stepper) {
            return;
        }

        if (this.currentStage === FlujoCreditoLightStages.EN_APROBACION) {
            this.stepper.selectedIndex = 1;
        }
    }

    setSelectedApprovalFlowItem(): void {
        if (!this.approvalFlow) return;

        const approvalFlow = this.approvalFlow?.approvalFlowResponse.definicionAprobaciones.flujoAprobaciones;

        this.selectedApprovalFlowItem = approvalFlow?.find((step: IApprovalFlowItem) => step.id === this.selectedStep);

        this.getSelectedTotalApprovedAmount();
    }

    getApprovalFlow(): void {
        this.flujoCreditoLightService.getApprovalFlow(this.currentIdTransaccion).subscribe(
            response => {
                if (Object.keys(response).length === 0) {
                    this.createApprovalFlow();
                    return;
                }

                this.approvalFlow = response;

                if (this.isFlowCompleted()) {
                    this.router.navigate(['/flujo-credito-light']);
                    this.alertService.info('El flujo de aprobación ha finalizado.');
                    return;
                }

                const etapaActual = this.approvalFlow?.approvalFlowResponse?.definicionAprobaciones?.etapaActual;

                this.currentStep = etapaActual;
                this.selectedStep = etapaActual;

                this.setSelectedApprovalFlowItem();
                this.getCurrentStep();
                this.getPreviousStep();
                this.setIsApproverUser();

                this.getMontosFields();
                this.generateCamposDatasource();
                this.getOtrosFields();

                this.lastTotalApprovedAmount = this.getTotalApprovedAmount();

                this.commentsForm.reset();
            },
            error => {
                this.alertService.error(`Error al obtener flujo de aprobación: ${error?.error?.message}`);
                goToTop();
            }
        );
    }

    setIsApproverUser(): void {
        if (!this.currentApprovalFlowItem) return;

        this.isApproverUser = this.currentApprovalFlowItem.configuracion.personasAprobadoras.some(person => person.id === this.currentUser);
    }

    createApprovalFlow(): void {
        const payload = {
            model: this.currentModel,
            idTransaccion: this.currentIdTransaccion
        };

        this.flujoCreditoLightService.createApprovalFlow(payload).subscribe(
            response => {
                this.approvalFlow = response;

                const etapaActual = this.approvalFlow?.approvalFlowResponse?.definicionAprobaciones?.etapaActual;

                this.currentStep = etapaActual;
                this.selectedStep = etapaActual;

                this.setSelectedApprovalFlowItem();
                this.getMontosFields();
                this.generateCamposDatasource();
                this.getOtrosFields();
                this.getCurrentStep();
                this.setIsApproverUser();

                this.commentsForm.reset();
            },
            error => {
                this.alertService.error(`Error al crear flujo de aprobación: ${error?.error?.message}`);
                goToTop();
            }
        );
    }

    getInputType(fieldType: string): string {
        switch (fieldType) {
            case 'NUM':
                return 'number';
            case 'STRING':
                return 'text';
            case 'DATE':
                return 'date';
            default:
                return 'text';
        }
    }

    generateCamposDatasource(): void {
        if (!this.selectedApprovalFlowItem) return;

        this.camposDataSource.data = this.selectedApprovalFlowItem.valores.montos.map((amount: IMonto) => {
            return {
                producto: amount.displayName || amount.nombre,
                formControlName: amount.nombre,
                inputType: this.getInputType(amount.tipo || 'NUM'),
                value: amount.valor,
            }
        }) || [];
    }

    getMontosFields(): void {
        if (!this.selectedApprovalFlowItem) return;

        this.montosForm = this.fb.group({});

        for (const field of this.selectedApprovalFlowItem.valores.montos) {
            let initialValue: number | null = null;

            if (this.previousApprovalFlowItem) {
                const prevField = this.previousApprovalFlowItem.valores.montos.find((amount: IMonto) => amount.nombre === field.nombre);
                
                if (prevField) {
                    const parsedValue = parseInt(prevField.valor ?? '');
                    initialValue = isNaN(parsedValue) ? null : parsedValue;
                }
            }

            this.montosForm.addControl(
                field.nombre,
                new FormControl<number | null>(initialValue, Validators.required)
            );
        }
    }

    getOtrosFields(): void {
        if (!this.selectedApprovalFlowItem) return;
        if (!this.selectedApprovalFlowItem.valores.otros) return;

        this.otrosForm.reset();

        for (const field of this.selectedApprovalFlowItem.valores.otros) {
            this.otrosForm.addControl(
                field.nombre,
                new FormControl(null, Validators.required)
            );
        }
    }

    updateApprovalFlow(isApproved: boolean): void {
        if (!this.isAdmin) return;
        if (!this.isApproverUser) return;
        if (!this.approvalFlow?.approvalFlowResponse.definicionAprobaciones.etapaActual) return;

        if (this.montosForm.valid && this.otrosForm.valid && this.commentsForm.valid) {

            if (isApproved) this.isApproveLoading = true;
            else this.isRejectLoading = true;

            const originalAmounts = this.selectedApprovalFlowItem?.valores.montos;

            const { ...amountsFromForm } = this.montosForm.value;
            const { ...otrosFromForm } = this.otrosForm.value;
            const { comments } = this.commentsForm.value;

            const amounts = Object.entries(amountsFromForm).map(([key, value]) => {
                const originalValue = originalAmounts?.find(amount => amount.nombre === key) as IMonto;

                return {
                    ...originalValue,
                    valor: value ? Number(value) : 0,
                }
            });

            const otros = Object.entries(otrosFromForm).map(([key, value]) => {
                let valorTransformado: string | number = '';
                if (value instanceof Date) {
                    valorTransformado = value.toISOString();
                } else if (typeof value === 'string' || typeof value === 'number') {
                    valorTransformado = value;
                }

               return {
                    nombre: key,
                    valor: valorTransformado || '',
                }
            });

            const payload = {
                idTx: this.currentIdTransaccion,
                modelo: this.currentModel,
                etapa: this.approvalFlow.approvalFlowResponse.definicionAprobaciones.etapaActual,
                version: this.currentVersion,
                decision: isApproved ? 'aprobado' : 'rechazado',
                atributos: {
                    aprobado: isApproved,
                    comentarios: comments || '',
                    montos: amounts,
                    otros: otros,
                }
            };

            this.flujoCreditoLightService.updateApprovalFlow(payload).subscribe(
                () => {
                    goToTop();

                    this.isApproveLoading = false;
                    this.isRejectLoading = false;

                    this.previousApprovalFlowItem = undefined;

                    this.openSnackBar(`Flujo de aprobación actualizado correctamente.`, 'Cerrar');
                    this.getApprovalFlow();
                },
                error => {
                    this.alertService.error(`Error al actualizar el flujo de aprobación: ${error?.error?.message}`);
                    goToTop();

                    this.isApproveLoading = false;
                    this.isRejectLoading = false;
                }
            );

        } else {
            this.alertService.error('Error al enviar el formulario. Corrige los campos que contengan algún error e intenta nuevamente.');
            goToTop();

            this.markAllAsTouched(this.montosForm);
            this.markAllAsTouched(this.otrosForm);
            this.markAllAsTouched(this.commentsForm);
        }

    }

    generatePDF() {
        this.isPDFLoading = true;
        this.pdfGeneratorService._generatePDF(this.currentIdTransaccion, this.PDF_REPORT_NAME).subscribe(
            () => {
                this.getPDF();
            },
            error => {
                this.alertService.error(`Error al generar el PDF: ${error?.error?.message}`);
                goToTop();

                this.isPDFLoading = false;
            }
        )
    }

    getPDF() {
        this.pdfGeneratorService._getPDF(this.currentIdTransaccion, this.PDF_REPORT_NAME).subscribe(
            response => {
                if (response.URLReporte) {
                    window.open(response.URLReporte, '_blank');
                }
                this.isPDFLoading = false;
            },
            error => {
                this.alertService.error(`Error al obtener el PDF: ${error?.error?.message}`);
                goToTop();

                this.isPDFLoading = false;
            }
        )
    }

    getApprovers(item: IApprovalFlowItem): string {
        const approvers = item?.configuracion?.personasAprobadoras;

        if (!approvers || approvers.length === 0) return '';

        let respuesta = ``;
        approvers.forEach((approver: { id: string }) => {
            respuesta += `${approver?.id || ''}\n`;
        });

        return respuesta;
    }

    validateRejectedStatus(): boolean {
        const approvalStatusList = this.approvalFlow?.approvalFlowResponse?.definicionAprobaciones?.estadosAprobacionales || [];
        const approvalFlow = this.approvalFlow?.approvalFlowResponse?.definicionAprobaciones?.flujoAprobaciones || [];

        const rejectedStatus = approvalStatusList
            .filter((status: IApprovalFlowState) => !status.aprobado)
            .map((status: IApprovalFlowState) => status.nombre) || [];

        const rejected = approvalFlow.find((item: IApprovalFlowItem) => rejectedStatus.includes(item?.valores?.aprobado));

        return !!rejected;
    }

    getStatus(step: IApprovalFlowItem): string {
        const currentStep = this.approvalFlow?.approvalFlowResponse?.definicionAprobaciones?.etapaActual;
        const approvalStatusList = this.approvalFlow?.approvalFlowResponse?.definicionAprobaciones?.estadosAprobacionales;
        const isStepApproved = step.valores.aprobado;
        const approvalStatus = approvalStatusList?.find((status: { nombre: string }) => status.nombre === isStepApproved);

        if (currentStep === step.id && !this.isFlowCompleted()) {
            return this.stepStatus.CURRENT;
        }

        if (!isStepApproved) {

            if (this.validateRejectedStatus()) this.stepStatus.DISABLED;

            return this.stepStatus.PENDING;
        }

        if (approvalStatus?.aprobado) return this.stepStatus.COMPLETED;

        if (!approvalStatus?.aprobado) return this.stepStatus.DENIED;

        return '';
    }

    setSelectedStep(step: IApprovalFlowItem): void {
        this.selectedStep = step.id;
        this.setSelectedApprovalFlowItem();
        this.generateCamposDatasource();
    }

    isCurrentStep() {
        if (!this.currentStep) return;
        if (!this.selectedStep) return;

        return this.selectedStep === this.currentStep;
    }

    isFlowCompleted() {
        if (!this.approvalFlow) return;

        return this.approvalFlow.approvalFlowResponse.definicionAprobaciones.completado;
    }

    getDetailsButtonText() {
        if (this.showDetailsStep2) return 'Ocultar detalle';
        if (this.isCurrentStep() && !this.isFlowCompleted()) return 'Ver detalle y evaluar';
        return 'Ver detalle';
    }

    getRequestStatus() {
        if (!this.approvalFlow) return '';

        if (this.approvalFlow.approvalFlowResponse.definicionAprobaciones.completado) {
            return 'Solicitud completada';
        }

        return 'Solicitud en progreso';
    }

    getCurrentStep() {
        if (!this.approvalFlow) return;
        if (!this.currentStep) return;

        this.currentApprovalFlowItem = this.approvalFlow.approvalFlowResponse.definicionAprobaciones.flujoAprobaciones.find((step: IApprovalFlowItem) => step.id === this.currentStep);
    }

    getPreviousStep() {
        if (!this.approvalFlow) return;
        if (!this.currentStep || this.currentStep === 1) return;

        const previousStep = this.currentStep - 1;

        this.previousApprovalFlowItem = this.approvalFlow.approvalFlowResponse.definicionAprobaciones.flujoAprobaciones.find((step: IApprovalFlowItem) => step.id === previousStep);
    }

    getTotalApprovedAmount(): number {
        if (!this.previousApprovalFlowItem) return 0;

        if (!this.isFlowCompleted()) {
            const amounts = this.previousApprovalFlowItem.valores.montos;
            return amounts.reduce((total, amount) => total + parseInt(amount.valor), 0);
        }

        if (!this.currentApprovalFlowItem) return 0;

        const amounts = this.currentApprovalFlowItem.valores.montos;
        return amounts.reduce((total, amount) => total + parseInt(amount.valor), 0);
    }

    updateCurrentTotalApprovedAmount(): void {
        const formTemp = this.montosForm.getRawValue();
        const formValuesArray = Object.values(formTemp) as (string | null)[];

        this.currentTotalApprovedAmount = formValuesArray.reduce((total, value) => {
            const parsed = parseInt(value ?? '');
            return total + (isNaN(parsed) ? 0 : parsed);
        }, 0);
    }

    getSelectedTotalApprovedAmount(): void {
        if (!this.selectedApprovalFlowItem) return;

        this.selectedTotalApprovedAmount = this.selectedApprovalFlowItem.valores.montos.reduce((total, amount) => {
            const parsed = parseInt(amount.valor ?? '');
            return total + (isNaN(parsed) ? 0 : parsed);
        }, 0);
    }

    markAllAsTouched(form: FormGroup): void {
        Object.values(form.controls).forEach(control => control.markAsTouched());
    }

    getRuleEngineFields(): void {
        this.flujoCreditoLightService.getRuleEngineFields(this.currentIdTransaccion, this.currentModel).subscribe(
            (fields) => {
                this.ruleEngineFields = fields;
                this.mapRuleEngineFields();
            },
            error => {
                this.alertService.error(`Error al obtener campos del Rule Engine: ${error?.error?.message}`);
                goToTop();
            }
        );
    }

    mapRuleEngineFields(): void {
        this.ruleEngineFieldsWithValue = this.ruleEngineFields.map((field: IRuleEngineField) => {
            const key = field.name as keyof IResultado;
            return {
                ...field,
                value: this.generalReport.calculos.resultado[key] ?? null,
            }
        });
    }
}
